import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import NavigationRoundedIcon from '@mui/icons-material/NavigationRounded';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';

import { getProjectWorkflow, ownerLabel, remainingTime, workflowText, WORKFLOW_STAGES } from '../../Domain/projectWorkflow';
import { useI18n } from '../../i18n/I18nProvider';

const ProjectPulse = ({ demand, role = 'Client', compact = false }) => {
  const { language, locale } = useI18n();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  const workflow = useMemo(() => getProjectWorkflow(demand, role, now), [demand, role, now]);
  const remaining = remainingTime(workflow.target, now);
  const targetDate = workflow.target
    ? new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(workflow.target)
    : '';
  const actor = ownerLabel(workflow.owner, role, language);

  return (
    <Shell $tone={workflow.tone} $compact={compact}>
      <Top>
        <div>
          <Eyebrow><NavigationRoundedIcon /> {language === 'en' ? 'Live project progress' : 'Avancement du projet'}</Eyebrow>
          <Headline>{workflowText(workflow.headline, language)}</Headline>
          <Detail>{workflowText(workflow.detail, language)}</Detail>
        </div>
        <ProgressValue>{workflow.progress}%</ProgressValue>
      </Top>

      <Rail aria-label={language === 'en' ? 'Project progress' : 'Progression du projet'}>
        <RailFill style={{ width: `${workflow.progress}%` }} />
        {WORKFLOW_STAGES.map((stage, index) => (
          <Stage key={stage.key} $done={index < workflow.stageIndex || workflow.complete} $active={index === workflow.stageIndex && !workflow.complete}>
            <Dot>{index < workflow.stageIndex || workflow.complete ? <CheckCircleRoundedIcon /> : index + 1}</Dot>
            {!compact && <StageLabel>{language === 'en' ? stage.en : stage.fr}</StageLabel>}
          </Stage>
        ))}
      </Rail>

      <NextRow>
        <Actor $self={actor === (language === 'en' ? 'Your action' : 'À vous')}><PersonRoundedIcon /> {actor}</Actor>
        <NextCopy>
          <span>{language === 'en' ? 'Next' : 'Prochaine étape'}</span>
          <strong>{workflowText(workflow.nextAction, language)}</strong>
        </NextCopy>
        {workflow.target && (
          <Eta>
            <AccessTimeRoundedIcon />
            <div>
              <span>{workflowText(workflow.targetLabel, language) || (language === 'en' ? 'Target' : 'Échéance')}</span>
              <strong>{workflowText(remaining, language) || targetDate}</strong>
              <small>{targetDate}</small>
            </div>
          </Eta>
        )}
      </NextRow>
    </Shell>
  );
};

const toneBorder = ({ $tone }) => ($tone === 'attention' ? '#e3c1b9' : $tone === 'success' ? '#b8d8c3' : $tone === 'progress' ? '#d8c59d' : '#ded8cf');
const toneBg = ({ $tone }) => ($tone === 'attention' ? '#fff8f5' : $tone === 'success' ? '#f3faf5' : $tone === 'progress' ? '#fffbf3' : '#faf9f7');

const Shell = styled.section`
  display:grid;gap:${({ $compact }) => ($compact ? '.65rem' : '.85rem')};padding:${({ $compact }) => ($compact ? '.8rem' : '1rem')};
  border:1px solid ${toneBorder};border-radius:1rem;background:${toneBg};overflow:hidden;
`;
const Top = styled.div`display:grid;grid-template-columns:minmax(0,1fr) auto;gap:.8rem;align-items:start;`;
const Eyebrow = styled.span`display:inline-flex;align-items:center;gap:.3rem;color:#8b6b38;font-size:.62rem;font-weight:900;letter-spacing:.08em;text-transform:uppercase;svg{font-size:.9rem;}`;
const Headline = styled.h3`margin:.28rem 0 .15rem;color:#161616;font-size:clamp(.92rem,2vw,1.08rem);line-height:1.25;`;
const Detail = styled.p`margin:0;color:#6e685f;font-size:.7rem;line-height:1.45;`;
const ProgressValue = styled.strong`display:grid;place-items:center;min-width:3.1rem;height:2rem;padding:0 .55rem;border-radius:999px;background:#111;color:#fff;font-size:.7rem;`;
const Rail = styled.div`position:relative;display:grid;grid-template-columns:repeat(8,minmax(0,1fr));gap:.15rem;padding-top:.15rem;&:before{content:'';position:absolute;left:1.25rem;right:1.25rem;top:.78rem;height:.18rem;border-radius:999px;background:#e6dfd5;}`;
const RailFill = styled.i`position:absolute;left:1.25rem;top:.78rem;height:.18rem;max-width:calc(100% - 2.5rem);border-radius:999px;background:#b38d51;transition:width .35s ease;`;
const Stage = styled.div`position:relative;z-index:1;display:grid;justify-items:center;gap:.28rem;min-width:0;color:${({$active,$done})=>$active?'#111':$done?'#71552d':'#a39b91'};`;
const Dot = styled.span`display:grid;place-items:center;width:1.35rem;height:1.35rem;border:2px solid currentColor;border-radius:50%;background:#fff;font-size:.52rem;font-weight:900;svg{font-size:1rem;}`;
const StageLabel = styled.span`max-width:100%;overflow:hidden;text-overflow:ellipsis;color:inherit;font-size:.52rem;font-weight:800;white-space:nowrap;@media(max-width:42rem){display:none;}`;
const NextRow = styled.div`display:grid;grid-template-columns:auto minmax(0,1fr) auto;gap:.7rem;align-items:center;padding-top:.65rem;border-top:1px solid rgba(0,0,0,.07);@media(max-width:42rem){grid-template-columns:auto minmax(0,1fr);}`;
const Actor = styled.span`display:inline-flex;align-items:center;gap:.25rem;padding:.4rem .55rem;border-radius:999px;background:${({$self})=>$self?'#111':'#eee9e1'};color:${({$self})=>$self?'#fff':'#5d554b'};font-size:.6rem;font-weight:900;white-space:nowrap;svg{font-size:.85rem;}`;
const NextCopy = styled.div`min-width:0;display:grid;gap:.12rem;span{color:#8b8379;font-size:.56rem;font-weight:850;text-transform:uppercase;}strong{color:#282521;font-size:.68rem;line-height:1.35;}`;
const Eta = styled.div`display:flex;align-items:center;gap:.4rem;min-width:7.8rem;padding:.48rem .58rem;border-radius:.75rem;background:#fff;color:#2b2926;box-shadow:0 .18rem .5rem rgba(0,0,0,.04);svg{color:#9a7440;font-size:1rem;}div{display:grid;}span{color:#8b8379;font-size:.52rem;text-transform:uppercase;}strong{font-size:.69rem;}small{color:#837c73;font-size:.52rem;}@media(max-width:42rem){grid-column:1/-1;width:100%;}`;

export default ProjectPulse;
